import { z } from 'zod';
import { captionStyleSchema } from './captions.ts';
import { DUBBING_TARGETS, LANGUAGE_CODES, TRANSLATABLE_LANGUAGES } from './languages.ts';
import { VOICE_IDS } from './voices.ts';

/* ------------------------------------------------------------------ */
/* Catalogue                                                           */
/* ------------------------------------------------------------------ */

export const TOOL_IDS = [
  'dubbing',
  'narrated-story',
  'commentary',
  'voiceover',
  'transcriber',
  'animation',
  'image-generator',
  'video-generator',
] as const;

export type ToolId = (typeof TOOL_IDS)[number];

export type ToolCategory = 'edit' | 'create' | 'audio';

/**
 * Dépendances d'un outil : `ml` = service IA (transcription/voix/traduction), `media-gen` = générateur d'images/vidéos
 * externe, `templates` = modèles animés installés (`pnpm templates:sync`).
 */
export type ToolRequirement = 'ml' | 'media-gen' | 'templates';

export interface ToolDefinition {
  id: ToolId;
  /** Nom court, formulé comme une action (affiché partout dans l'interface). */
  name: string;
  /** Une phrase simple qui dit ce qu'on obtient. */
  summary: string;
  /** Libellé du bouton de lancement. */
  cta: string;
  category: ToolCategory;
  requires: ToolRequirement[];
  /** Coût indicatif, en mots simples. */
  costHint: string;
  /** Mis en avant sur l'accueil. */
  popular?: boolean;
}

export const TOOLS: ToolDefinition[] = [
  {
    id: 'dubbing',
    name: 'Traduire ma vidéo',
    summary: 'Ta vidéo parle une autre langue, avec une nouvelle voix et des sous-titres.',
    cta: 'Traduire la vidéo',
    category: 'edit',
    requires: ['ml'],
    costHint: '6 crédits par minute de vidéo',
    popular: true,
  },
  {
    id: 'narrated-story',
    name: 'Créer une vidéo 2D',
    summary: 'Colle ton histoire : elle devient un dessin animé raconté, avec décors et sous-titres.',
    cta: 'Créer la vidéo 2D',
    category: 'create',
    requires: ['ml'],
    costHint: '4 crédits',
  },
  {
    id: 'commentary',
    name: 'Vidéo à partir d’un texte',
    summary: 'Ton texte est lu à voix haute, avec une image ou un clip pour chaque partie.',
    cta: 'Créer la vidéo',
    category: 'create',
    requires: ['ml'],
    costHint: '1 crédit par partie',
  },
  {
    id: 'voiceover',
    name: 'Créer une voix off',
    summary: 'Écris un texte, récupère un fichier audio lu par une voix.',
    cta: 'Créer la voix',
    category: 'audio',
    requires: ['ml'],
    costHint: '1 crédit pour 500 caractères',
  },
  {
    id: 'transcriber',
    name: 'Récupérer le texte d’une vidéo',
    summary: 'Tout ce qui est dit dans la vidéo, en texte à copier ou en fichier de sous-titres.',
    cta: 'Récupérer le texte',
    category: 'audio',
    requires: ['ml'],
    costHint: '1 crédit par minute de vidéo',
  },
  {
    id: 'animation',
    name: 'Créer une animation',
    summary:
      'Choisis un modèle animé (titres, chiffres, carrousels, fonds…), mets ton texte, tes couleurs et tes images.',
    cta: 'Créer l’animation',
    category: 'create',
    requires: ['templates'],
    costHint: '2 crédits',
    popular: true,
  },
  {
    id: 'image-generator',
    name: 'Créer une image',
    summary: 'Décris une image, l’IA la dessine.',
    cta: 'Créer l’image',
    category: 'create',
    requires: ['media-gen'],
    costHint: 'Pas encore disponible',
  },
  {
    id: 'video-generator',
    name: 'Créer une vidéo avec l’IA',
    summary: 'Décris une scène, l’IA la filme.',
    cta: 'Créer la vidéo',
    category: 'create',
    requires: ['media-gen'],
    costHint: 'Pas encore disponible',
  },
];

export function findTool(id: string): ToolDefinition | undefined {
  return TOOLS.find((t) => t.id === id);
}

/**
 * Outils retirés : on ne peut plus les lancer, mais les vidéos déjà créées avec eux restent visibles et
 * téléchargeables dans « Mes vidéos ».
 */
export const RETIRED_TOOLS = {
  captions: 'Ajouter des sous-titres',
  'auto-clip': 'Couper en shorts',
  'split-screen': 'Deux vidéos l’une sur l’autre',
  ranking: 'Faire un top',
  'chat-story': 'Fausse conversation',
} as const;

export type RetiredToolId = keyof typeof RETIRED_TOOLS;

/** Nom affiché d'un outil, retiré compris (anciennes créations). */
export function toolName(id: string): string {
  return findTool(id)?.name ?? RETIRED_TOOLS[id as RetiredToolId] ?? 'Outil retiré';
}

/* ------------------------------------------------------------------ */
/* Schémas d'entrée                                                    */
/* ------------------------------------------------------------------ */

const id = z.string().min(1).max(64);
const languageOrAuto = z.enum(['auto', ...LANGUAGE_CODES]).default('auto');
const voice = z.enum(VOICE_IDS);
const speed = z.number().min(0.7).max(1.5).default(1);
const format = z.enum(['vertical', 'original']).default('vertical');

export const transcriberInput = z.object({
  assetId: id,
  language: languageOrAuto,
});

export const voiceoverInput = z.object({
  text: z.string().trim().min(1).max(5000),
  voice,
  speed,
});

export const dubbingInput = z
  .object({
    assetId: id,
    sourceLanguage: z.enum(['auto', ...TRANSLATABLE_LANGUAGES] as [string, ...string[]]).default('auto'),
    targetLanguage: z.enum(DUBBING_TARGETS as [string, ...string[]]),
    voice,
    /** duck : voix d'origine en fond très bas ; remove : voix d'origine supprimée. */
    originalAudio: z.enum(['duck', 'remove']).default('duck'),
    subtitles: z.boolean().default(true),
    style: captionStyleSchema.default(captionStyleSchema.parse({ maxWordsPerLine: 4, maxLines: 2 })),
  })
  .refine((v) => v.sourceLanguage !== v.targetLanguage, {
    message: 'La langue cible doit être différente de la langue source',
    path: ['targetLanguage'],
  });

/** Personnage principal d'une vidéo 2D (mêmes identifiants que la troupe du moteur 2D). */
export const STORY_CHARACTERS = [
  { id: 'hero', label: 'Un homme' },
  { id: 'heroine', label: 'Une femme' },
  { id: 'kid', label: 'Un enfant' },
  { id: 'elder', label: 'Un ancien' },
  { id: 'worker', label: 'Un ouvrier' },
] as const;
export type StoryCharacterId = (typeof STORY_CHARACTERS)[number]['id'];

/** Vidéo 2D : texte lu, mis en scène automatiquement (décors, personnage, icônes, sous-titres). */
export const narratedStoryInput = z.object({
  title: z.string().trim().max(80).optional(),
  /** Petite pastille au-dessus du titre (« Épisode 1 »). */
  kicker: z.string().trim().max(24).optional(),
  story: z.string().trim().min(10).max(3000),
  character: z
    .enum(STORY_CHARACTERS.map((c) => c.id) as [StoryCharacterId, ...StoryCharacterId[]])
    .default('hero'),
  voice,
  speed,
});

export const commentaryInput = z.object({
  scenes: z
    .array(z.object({ text: z.string().trim().min(1).max(600), assetId: id.optional() }))
    .min(1)
    .max(30),
  voice,
  speed,
  format,
  captions: z.boolean().default(true),
  style: captionStyleSchema.default(captionStyleSchema.parse({})),
});

export const animationInput = z.object({
  template: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .max(80),
  /** Valeurs des champs du modèle, vérifiées par l'API selon le modèle choisi. */
  values: z.record(z.string(), z.union([z.string().max(2000), z.number(), z.boolean()])).default({}),
  /** Images de l'utilisateur, placées dans l'ordre dans les emplacements du modèle. */
  images: z.array(id).max(24).default([]),
  /** vertical : une animation horizontale est posée sur un fond flouté en 9:16. */
  format: z.enum(['original', 'vertical']).default('original'),
});

/** Schéma d'entrée par outil (outils implémentés uniquement). */
export const TOOL_INPUTS = {
  dubbing: dubbingInput,
  'narrated-story': narratedStoryInput,
  commentary: commentaryInput,
  voiceover: voiceoverInput,
  transcriber: transcriberInput,
  animation: animationInput,
} as const;

export type ImplementedToolId = keyof typeof TOOL_INPUTS;

export function isImplementedTool(id: string): id is ImplementedToolId {
  return id in TOOL_INPUTS;
}

export type ToolInput<T extends ImplementedToolId> = z.infer<(typeof TOOL_INPUTS)[T]>;
export type VoiceoverInput = z.infer<typeof voiceoverInput>;
export type DubbingInput = z.infer<typeof dubbingInput>;
export type NarratedStoryInput = z.infer<typeof narratedStoryInput>;
export type CommentaryInput = z.infer<typeof commentaryInput>;
export type TranscriberInput = z.infer<typeof transcriberInput>;
export type AnimationInput = z.infer<typeof animationInput>;
